import React, { createContext, useContext, useState, useCallback } from "react";
import ReactDOM from "react-dom";
import "./Toast.css";

// ─────────────────────────────────────────────────────────────────────────────
// TOAST GLOBAL — couche de notification unique, garantie AU-DESSUS de TOUT (modales,
// dialogues de confirmation, etc.) :
//   • Rendu via un PORTAL sur document.body → échappe à tout stacking-context local
//     (une modale ne peut plus le recouvrir), avec un z-index maximal (voir Toast.css).
//   • AUTO-DISMISS par type (succès ~4 s, info ~5 s, avertissement ~8 s, erreur ~12 s).
//     Une erreur VRAIMENT bloquante qui exige une action peut forcer la persistance via
//     `duration: 0`. Un toast reste indépendant du composant qui l'a déclenché (il survit
//     à la fermeture d'une modale), mais il n'est plus « éternel » par défaut.
//   • Fermeture : ✕ manuel, ou timer, ou programmatique (clear / removeByKey).
//   • Corrélation Alert↔Toast : un toast créé par une Alert porte le même `key` → fermer
//     l'un ferme l'autre (pas de message affiché deux fois sous deux formes). Voir Alert.js.
//   • Plusieurs toasts s'empilent ; les doublons (même type+message) ne se ré-empilent pas.
// ─────────────────────────────────────────────────────────────────────────────

const ToastContext = createContext();
export const useToast = () => useContext(ToastContext);

let _id = 0;
// Auto-dismiss par type (ms). Les ERREURS NON BLOQUANTES disparaissent seules au bout de ~12 s
// (assez pour lire) → fini le « reste affiché indéfiniment ». Une erreur VRAIMENT bloquante qui
// exige une action peut forcer la persistance en passant explicitement `duration: 0`.
const AUTO_DISMISS = { success: 4000, info: 5000, warning: 8000, error: 12000 };
const MAX_TOASTS = 4; // limite d'empilement : au-delà, on retire le plus ancien.

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  // Timers par id → annulés à la fermeture/au démontage (pas de fuite ni de setState post-unmount).
  const timersRef = React.useRef(new Map());

  // Notifie (via un évènement window) que le toast portant `key` a disparu → l'Alert jumelle,
  // qui écoute cette clé, se masque aussi. Pas de dépendance directe entre les 2 composants.
  const emitDismissed = (key) => {
    if (!key || typeof window === "undefined") return;
    try { window.dispatchEvent(new CustomEvent("gowele:toast-dismissed", { detail: { key } })); } catch (_) {}
  };

  const remove = useCallback((id) => {
    const tmr = timersRef.current.get(id);
    if (tmr) { clearTimeout(tmr); timersRef.current.delete(id); }
    setToasts((list) => {
      const gone = list.find((t) => t.id === id);
      if (gone?.key) emitDismissed(gone.key);
      return list.filter((t) => t.id !== id);
    });
  }, []);

  // Ferme le(s) toast(s) portant une clé de corrélation (fermeture d'une Alert jumelle).
  const removeByKey = useCallback((key) => {
    if (!key) return;
    setToasts((list) => {
      list.forEach((t) => {
        if (t.key === key) { const tm = timersRef.current.get(t.id); if (tm) { clearTimeout(tm); timersRef.current.delete(t.id); } }
      });
      return list.filter((t) => t.key !== key);
    });
  }, []);

  const show = useCallback(({ type = "info", message, duration, key } = {}) => {
    if (!message) return null;
    const id = ++_id;
    let created = true;
    setToasts((list) => {
      // Dédoublonnage : un message identique déjà affiché n'est pas ré-empilé (évite le spam,
      // notamment quand l'interceptor axios ET l'Alert du composant remontent la même erreur).
      if (list.some((tt) => tt.type === type && tt.message === message)) { created = false; return list; }
      const next = [...list, { id, type, message, key }];
      // Limite d'empilement : on retire les plus anciens au-delà de MAX_TOASTS.
      return next.length > MAX_TOASTS ? next.slice(next.length - MAX_TOASTS) : next;
    });
    if (!created) return null; // doublon → pas de nouveau timer
    const ms = duration !== undefined ? duration : (AUTO_DISMISS[type] ?? 0);
    if (ms > 0) timersRef.current.set(id, setTimeout(() => remove(id), ms));
    return id;
  }, [remove]);

  // Nettoyage global au démontage du provider (tous les timers en attente).
  React.useEffect(() => () => { timersRef.current.forEach(clearTimeout); timersRef.current.clear(); }, []);

  // Vide tous les toasts (ou ceux d'un type) — utilisé au login réussi / changement de session
  // pour supprimer les anciennes erreurs d'authentification.
  const clear = useCallback((type) => {
    setToasts((list) => {
      const kept = type ? list.filter((t) => t.type !== type) : [];
      // annule les timers des toasts retirés
      list.forEach((t) => { if (!kept.includes(t)) { const tm = timersRef.current.get(t.id); if (tm) { clearTimeout(tm); timersRef.current.delete(t.id); } } });
      return kept;
    });
  }, []);

  const portalTarget = typeof document !== "undefined" ? document.body : null;

  return (
    <ToastContext.Provider value={{ show, clear, removeByKey }}>
      {children}
      {portalTarget && ReactDOM.createPortal(
        <div className="toast-container" role="region" aria-live="assertive" aria-atomic="true">
          {toasts.map((tt) => (
            <div key={tt.id} className={`toast toast-${tt.type}`} role="alert">
              <span className="toast-msg">{tt.message}</span>
              <button className="toast-close" onClick={() => remove(tt.id)} aria-label="Fermer">✕</button>
            </div>
          ))}
        </div>,
        portalTarget
      )}
    </ToastContext.Provider>
  );
};

// Compat : Toast.show(...) → passe par l'injection globale.
export const Toast = {
  show: ({ type, message, duration, key } = {}) =>
    window.__toast && window.__toast({ type, message, duration, key }),
};

// Injection globale (utilisée par l'interceptor axios et le pont Alert→Toast).
window.__toast = null;
window.__toastClear = null;       // vide les toasts (ex. au login réussi) — supprime les anciennes erreurs.
window.__toastRemoveByKey = null; // ferme le toast jumeau d'une Alert (corrélation par clé).

export const ToastInitializer = () => {
  const { show, clear, removeByKey } = useToast();
  window.__toast = show;       // disponible immédiatement, `show` est stable (useCallback)
  window.__toastClear = clear; // idem pour le nettoyage
  window.__toastRemoveByKey = removeByKey; // fermeture ciblée par clé de corrélation
  return null;
};

export default Toast;
